import { useCallback, useEffect, useState } from "react";
import type { Language } from "./types";
import { AppHeader } from "./components/AppHeader";
import SurveyPage from "./pages/SurveyPage";
import PikbSurveyPage from "./pages/PikbSurveyPage";
import DashboardPage from "./pages/DashboardPage";
import packageInfo from "../package.json";

const appBasePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function toAppPath(pathname: string) {
  if (!appBasePath || appBasePath === "/") return pathname;
  if (pathname === appBasePath) return "/";
  if (pathname.startsWith(`${appBasePath}/`)) {
    return pathname.slice(appBasePath.length) || "/";
  }
  return pathname;
}

function toBrowserPath(path: string) {
  if (!appBasePath || appBasePath === "/") return path;
  return `${appBasePath}${path === "/" ? "" : path}`;
}

function usePath() {
  const [path, setPath] = useState(() => toAppPath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setPath(toAppPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((nextPath: string) => {
    const browserPath = toBrowserPath(nextPath);
    if (window.location.pathname === browserPath) return;
    window.history.pushState({}, "", browserPath);
    setPath(nextPath);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  }, []);

  return { path, navigate };
}

function useVersionRefresh() {
  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`,
          {
          cache: "no-store",
          },
        );
        if (!response.ok) return;

        const deployed = (await response.json()) as { version?: string };
        if (
          !cancelled &&
          deployed.version &&
          deployed.version !== packageInfo.version
        ) {
          window.location.reload();
        }
      } catch {
        // Keep the survey usable if the version probe is blocked or offline.
      }
    };

    void checkVersion();
    const interval = window.setInterval(checkVersion, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);
}

export default function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = window.localStorage.getItem("putatan_survey_language");
    return saved === "en" ? "en" : "ms";
  });
  const { path, navigate } = usePath();
  const isDashboard = path === "/" || path.startsWith("/dashboard");
  const isPikb = path.startsWith("/pikb");
  const isPutatanSurvey = path.startsWith("/putatan") || (!isDashboard && !isPikb);

  useVersionRefresh();

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem("putatan_survey_language", language);
  }, [language]);

  return (
    <div className="app-shell" data-app-version={packageInfo.version}>
      {isPutatanSurvey ? (
        <AppHeader
          language={language}
          path={path}
          navigate={navigate}
          setLanguage={setLanguage}
          showNavigation={false}
        />
      ) : null}

      {isDashboard ? (
        <DashboardPage
          language={language}
          navigate={navigate}
          setLanguage={setLanguage}
        />
      ) : isPikb ? (
        <PikbSurveyPage />
      ) : (
        <SurveyPage language={language} setLanguage={setLanguage} />
      )}
    </div>
  );
}
